import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './SignIn.css';

const API_URL = 'https://zangena-e33a7e55637a.herokuapp.com/api';

const SignIn = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [message, setMessage] = useState('');
  const [showTotp, setShowTotp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const isPhoneNumber = useCallback((input) => {
    const trimmedInput = input.trim();
    const zambianNumberPattern = /^(0?[976][56789]|7[34679])\d{7}$/;
    return zambianNumberPattern.test(trimmedInput);
  }, []);

  const normalizePhoneNumber = useCallback((input) => {
    const trimmedInput = input.trim();
    return `+260${trimmedInput.replace(/^0/, '')}`;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsLoading(true);

    if (!identifier.trim() || !password.trim()) {
      setMessage('Please enter your username, phone number, or email and password.');
      setIsLoading(false);
      return;
    }
    if (showTotp && !totpCode.match(/^\d{6}$/)) {
      setMessage('Please enter a valid 6-digit 2FA code.');
      setIsLoading(false);
      return;
    }

    const normalizedIdentifier = isPhoneNumber(identifier)
      ? normalizePhoneNumber(identifier)
      : identifier.trim();

    try {
      const response = await axios.post(`${API_URL}/users/login`, {
        identifier: normalizedIdentifier,
        password: password.trim(),
        ...(showTotp && { totpCode: totpCode.trim() }),
      }, {
        headers: { Accept: 'application/json' },
        timeout: 45000,
      });

      const data = response.data;

      if (data.twoFactorEnabled && !showTotp) {
        setShowTotp(true);
        setMessage('Please enter your 2FA code.');
        setIsLoading(false);
        return;
      }

      if (data.role !== 'admin') {
        setMessage('Access denied: This system is for administrators only.');
        setIsLoading(false);
        return;
      }

      if (!data.isActive) {
        setMessage('Your account is inactive. Please contact support.');
        setIsLoading(false);
        return;
      }

      if (data.role !== 'admin' && data.kycStatus !== 'verified') {
        setMessage(data.kycStatus === 'rejected' ? 'Account rejected.' : 'Account pending verification.');
        setIsLoading(false);
        return;
      }

      localStorage.setItem('user', JSON.stringify({
        username: data.username,
        phoneNumber: data.phoneNumber,
        role: data.role,
        name: data.name,
        kycStatus: data.kycStatus,
        isFirstLogin: data.isFirstLogin,
        isActive: data.isActive,
        twoFactorEnabled: data.twoFactorEnabled
      }));
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);

      setMessage('Sign-in successful!');
      setIdentifier('');
      setPassword('');
      setTotpCode('');
      setShowTotp(false);
      setShowPassword(false);

      navigate('/admin');
    } catch (error) {
      console.error('[SignIn] Error:', error.response?.data || error.message);
      let errorMessage = 'Sign-in failed. Please try again.';
      if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
        errorMessage = 'Unable to connect to server. Please try again later.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.error || 'Invalid credentials.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please check your credentials.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many attempts. Please wait a few minutes.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Contact support@fugipay.com.';
      }
      setMessage(errorMessage);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="signin-page">
      <div className="signin-container">
        <h1 className="signin-title">FugiPay Admin Sign-In</h1>
        <form className="signin-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="identifier">Username, Phone Number, or Email</label>
            <input
              type="text"
              id="identifier"
              className="form-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Enter username, phone (e.g., 0777210641), or email"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          {showTotp && (
            <div className="form-group">
              <label htmlFor="totpCode">2FA Code</label>
              <input
                type="text"
                id="totpCode"
                className="form-input"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="Enter 6-digit code"
                disabled={isLoading}
              />
            </div>
          )}
          {message && <p className="message">{message}</p>}
          <button type="submit" className="signin-button" disabled={isLoading}>
            {isLoading && <span className="spinner"></span>}
            Sign In
          </button>
          {/* <a href="mailto:support@fugipay.com" className="forgot-password-link">
          Forgot Password?
        </a> */}
        </form>
      </div>

      {/* ────── FOOTER (sticks to the bottom) ────── */}

    </div>
  );
};

export default SignIn;